import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  MenuList,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Skeleton,
  Chip,
  Badge,
  Divider,
  TextField,
  InputAdornment,
  Fade,
  Zoom,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { factory } from "../../../api/apiFactory";
import L from "leaflet";
import { FaMapMarkerAlt } from "react-icons/fa";
import Dayjs from "../../../helpers/dayjs";

const usersApi = factory.get("users");

const UsersMapLocationsDialog = ({
  open,
  onClose,
  centerId,
  params = null,
}) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState(null);
  const markerRef = useRef({});
  const [firstLoad, setFirstLoad] = useState(false);
  const [typeFilter, setTypeFilter] = useState("collectors");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const createMarkerIcon = (isOnline) => {
    const color = isOnline ? "#4CAF50" : "#f44336";
    const shadowColor = isOnline
      ? "rgba(76, 175, 80, 0.3)"
      : "rgba(244, 67, 54, 0.3)";

    const svgIcon = `
      <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="shadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="${shadowColor}" flood-opacity="0.8"/>
          </filter>
        </defs>
        <path d="M16 0C7.163 0 0 7.163 0 16c0 8.837 16 26 16 26s16-17.163 16-26C32 7.163 24.837 0 16 0z" 
              fill="${color}" filter="url(#shadow)"/>
        <circle cx="16" cy="16" r="8" fill="white"/>
        <circle cx="16" cy="16" r="5" fill="${color}"/>
        ${isOnline ? '<circle cx="16" cy="16" r="2" fill="white"/>' : ""}
      </svg>
    `;

    return L.divIcon({
      html: svgIcon,
      className: "custom-marker",
      iconSize: [32, 42],
      iconAnchor: [16, 42],
      popupAnchor: [0, -42],
    });
  };

  const fetchDrivers = useCallback(() => {
    if (!open) return;
    setLoading(true);
    usersApi
      .liveLocation({
        type: typeFilter,
      })
      .then((response) => {
        if (!firstLoad) {
          setFirstLoad(true);
        }
        setUsers(response.data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [open, typeFilter, firstLoad]);

  useEffect(() => {
    fetchDrivers();
    if (autoRefresh) {
      const interval = setInterval(fetchDrivers, 15000);
      return () => clearInterval(interval);
    }
  }, [fetchDrivers, autoRefresh]);

  const filteredUsers = useMemo(() => {
    let filtered = users;

    if (statusFilter === "online") {
      filtered = filtered.filter((user) => user.is_online);
    } else if (statusFilter === "offline") {
      filtered = filtered.filter((user) => !user.is_online);
    }

    if (searchQuery.trim()) {
      filtered = filtered.filter((user) =>
        user.user_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [users, statusFilter, searchQuery]);

  const onlineCount = users.filter((user) => user.is_online).length;
  const offlineCount = users.length - onlineCount;

  const closeAllPopups = () => {
    if (!map) return;
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        layer.closePopup();
      }
    });
  };

  const onClickShowMarker = (user) => {
    if (!map) return;

    if (user?.id === selectedUser?.id) {
      setSelectedUser(null);
      closeAllPopups();
      map.setView([user.latitude, user.longitude], 13);
      return;
    }

    setSelectedUser(user);
    map.flyTo([user.latitude, user.longitude], 16);
    closeAllPopups();

    setTimeout(() => {
      const marker = markerRef.current[user.id];
      if (marker) {
        marker.openPopup();
      }
    }, 800);
  };

  useEffect(() => {
    if (selectedUser && map) {
      setTimeout(() => {
        const marker = markerRef.current[selectedUser.id];
        if (marker) {
          marker.openPopup();
        }
      }, 300);
    }
  }, [selectedUser, filteredUsers, map]);

  useEffect(() => {
    setSelectedUser(null);
    closeAllPopups();
  }, [typeFilter, statusFilter]);

  const handleRefresh = () => {
    fetchDrivers();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dir="rtl"
      fullWidth
      fullScreen
      maxWidth="xl"
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, zIndex: 1 }}>
          <LocationOnIcon sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">
            {params?.driver_id
              ? `موقع الموظف : ${params.user?.user_name || params?.user_name}`
              : "خريطة مواقع الموظفين"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, zIndex: 1 }}>
          <Tooltip title="تحديث">
            <IconButton
              onClick={handleRefresh}
              sx={{ color: "white" }}
              disabled={loading}
            >
              <RefreshIcon className={loading ? "animate-spin" : ""} />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", height: "100%" }}>
        {loading && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2000,
              height: 3,
            }}
          />
        )}

        <Box sx={{ position: "relative", height: "100%" }}>
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              top: 20,
              left: 20,
              zIndex: 1100,
              padding: 3,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              maxHeight: "calc(100vh - 200px)",
              overflowY: "auto",
              borderRadius: 3,
              boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              minWidth: 320,
              maxWidth: 380,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                color="primary"
              >
                إحصائيات سريعة
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip
                  icon={<PersonIcon />}
                  label={`المتصلين: ${onlineCount}`}
                  color="success"
                  variant="filled"
                  size="small"
                />
                <Chip
                  icon={<PersonIcon />}
                  label={`غير متصلين: ${offlineCount}`}
                  color="error"
                  variant="filled"
                  size="small"
                />
                <Chip
                  label={`الإجمالي: ${users.length}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                نوع الموظف
              </Typography>
              <ToggleButtonGroup
                value={typeFilter}
                exclusive
                onChange={(e, val) => val && setTypeFilter(val)}
                sx={{ width: "100%" }}
                color="primary"
                size="small"
              >
                <ToggleButton value="collectors" sx={{ flex: 1 }}>
                  جباية
                </ToggleButton>
                <ToggleButton value="maintenance" sx={{ flex: 1 }}>
                  صيانة
                </ToggleButton>
                <ToggleButton value="sales" sx={{ flex: 1 }}>
                  مبيعات
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                حالة الاتصال
              </Typography>
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, val) => val && setStatusFilter(val)}
                sx={{ width: "100%" }}
                color="secondary"
                size="small"
              >
                <ToggleButton value="all" sx={{ flex: 1 }}>
                  الكل
                </ToggleButton>
                <ToggleButton value="online" sx={{ flex: 1 }}>
                  متصل
                </ToggleButton>
                <ToggleButton value="offline" sx={{ flex: 1 }}>
                  غير متصل
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="بحث عن موظف..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              قائمة الموظفين ({filteredUsers.length})
            </Typography>
            <MenuList sx={{ maxHeight: 300, overflowY: "auto" }}>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <MenuItem key={index} sx={{ borderRadius: 2, mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                    </Box>
                  </MenuItem>
                ))
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => {
                  const isOnline = user.is_online;
                  const isSelected = selectedUser?.user_id === user.user_id;

                  return (
                    <Zoom key={user.id} in={true} timeout={index * 100}>
                      <MenuItem
                        onClick={() => onClickShowMarker(user)}
                        selected={isSelected}
                        sx={{
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "primary.light",
                            // color: "white",
                            transform: "translateX(-5px)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          },
                          // ...(isSelected && {
                          //   backgroundColor: "primary.main",
                          //   color: "white",
                          //   boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                          // }),
                          borderRadius: 2,
                          padding: 2,
                          mb: 1,
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "100%",
                            zIndex: 1,
                          }}
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: isOnline
                                    ? "#4CAF50"
                                    : "#f44336",
                                  border: "2px solid white",
                                  boxShadow: isOnline
                                    ? "0 0 8px rgba(76, 175, 80, 0.8)"
                                    : "0 0 8px rgba(244, 67, 54, 0.8)",
                                }}
                              />
                            }
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                background: `linear-gradient(135deg, ${
                                  isOnline ? "#4CAF50" : "#f44336"
                                } 0%, ${
                                  isOnline ? "#81C784" : "#EF5350"
                                } 100%)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                              }}
                            >
                              {index + 1}
                            </Box>
                          </Badge>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {user.user_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: isSelected ? 1 : 0.7,
                                color: isSelected
                                  ? "inherit"
                                  : isOnline
                                  ? "success.main"
                                  : "error.main",
                              }}
                            >
                              {isOnline ? "متصل الآن" : "غير متصل"}
                            </Typography>
                            <Typography variant="caption" color={"gray"}>
                              - {Dayjs(user.created_at).fromNow()}
                            </Typography>
                          </Box>
                          {isSelected && (
                            <LocationOnIcon
                              sx={{ color: "inherit", fontSize: 20 }}
                            />
                          )}
                        </Box>

                        {isSelected && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              backgroundColor: "white",
                              borderRadius: "0 4px 4px 0",
                            }}
                          />
                        )}
                      </MenuItem>
                    </Zoom>
                  );
                })
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <PersonIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                  <Typography color="textSecondary">
                    {searchQuery ? "لا توجد نتائج للبحث" : "لا يوجد موظفين"}
                  </Typography>
                </Box>
              )}
            </MenuList>
          </Paper>

          <MapContainer
            center={
              filteredUsers.length > 0 && firstLoad
                ? [filteredUsers[0].latitude, filteredUsers[0].longitude]
                : selectedUser
                ? [selectedUser.latitude, selectedUser.longitude]
                : [33.3152, 44.3661]
            }
            zoom={params?.user_id ? 15 : 11}
            style={{ height: "100vh", width: "100%" }}
            zoomControl={false}
            ref={(mapInstance) => mapInstance && setMap(mapInstance)}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <ZoomControl position="bottomright" />

            {filteredUsers.map((user, index) => (
              <Marker
                key={`${user.id}-${user.is_online}`}
                position={[user.latitude, user.longitude]}
                icon={createMarkerIcon(user.is_online)}
                ref={(marker) => {
                  if (marker) markerRef.current[user.id] = marker;
                }}
              >
                <Popup
                  closeButton={true}
                  autoClose={false}
                  closeOnEscapeKey={true}
                >
                  <Box sx={{ textAlign: "center", p: 1 }}>
                    <Chip
                      variant="outlined"
                      label={user.is_online ? "متصل الآن" : "غير متصل"}
                      color={user.is_online ? "success" : "error"}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Stack
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"center"}
                    >
                      <Tooltip title="فتح في خرائط جوجل">
                        <IconButton
                          variant="contained"
                          size="small"
                          color="info"
                          onClick={() => {
                            const latitude = user.latitude;
                            const longitude = user.longitude;
                            window.open(
                              `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
                            );
                          }}
                        >
                          <FaMapMarkerAlt />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="h5" fontWeight="bold">
                        {user.user_name}
                      </Typography>
                    </Stack>

                    <Typography variant="caption" color="textSecondary">
                      آخر تحديث:{" "}
                      {Dayjs(user.created_at).format("DD -MM / HH:mm A")}
                    </Typography>
                  </Box>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {filteredUsers.length === 0 && !loading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                padding: 4,
                borderRadius: 3,
                boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.15)",
              }}
            >
              <LocationOnIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h5" color="primary" fontWeight="bold">
                لا توجد مواقع للموظفين
              </Typography>
              <Typography color="textSecondary" sx={{ mt: 1 }}>
                {searchQuery
                  ? "جرب البحث بكلمة مختلفة"
                  : "لا يوجد موظفين متاحين حالياً"}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          .custom-marker {
            background: none !important;
            border: none !important;
          }
        `}
      </style>
    </Dialog>
  );
};

export default UsersMapLocationsDialog;
